
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { addBlinkAnimation } from '@/utils/general';
import { getTimeAgo } from '@/utils/format';
import { Debounce, cancelDebounce } from '@/utils/decorators';
import Toast from '@/utils/toast';
import SelectComponent from '@/components/shared/Select.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';

const namespace: string = 'dashboard';

@Component<PendingOrders>({
	components: {
		SelectComponent,
		TextButton
	}
})
export default class PendingOrders extends Vue {
	@Prop({ type: Boolean, default: false }) private areThereNewOrders!: boolean;
	@Action('setPendingOrdersSort', { namespace }) private setPendingOrdersSort!: (sortBy: 'created_at' | 'pickup_time') => Promise<void>;
	@Action('setPendingOrdersOffset', { namespace }) private setPendingOrdersOffset!: (offset: number) => Promise<void>;
	@Getter('getPendingOrders', { namespace }) private pendingOrders!: DashboardOrder[];
	@Getter('getPendingOrdersCount', { namespace }) private pendingOrdersCount!: number;
	@Getter('getPendingOrderSort', { namespace }) private pendingOrderSort!: 'created_at' | 'pickup_time';
	@Watch('areThereNewOrders')
	onNewOrders(newValue: boolean) {
		if (newValue) {
			addBlinkAnimation(this.$refs.pendingOrdersHeader, 'new-orders', 3);
		}
	}

	$refs!: {
		pendingOrdersHeader: HTMLElement;
		pendingOrdersList: HTMLElement;
		pendingOrderLoading: HTMLElement;
	};

	private changingSortType: boolean = false;
	private pendingOrdersFetching: boolean = false;
	private errorFetchingMoreOrders: boolean = false;
	private sortBy: string = '';
	private sortOptions = [
		{ text: this.$t('orders.dashboard.pending_orders.sort_options.latest'), value: 'created_at' },
		{ text: this.$t('orders.dashboard.pending_orders.sort_options.upcoming'), value: 'pickup_time' }
	];

	private get morePendingOrdersToLoad(): boolean {
		return this.pendingOrdersCount > this.pendingOrders.length;
	}

	/**
	 * A check for if more orders should be fetched on scroll
	 * Orders if there are more to fetch, orders aren't already being fetched and there are no errors from fetching
	 *
	 * @return {boolean}
	 */
	private get loadMoreOrdersOnScroll(): boolean {
		return this.morePendingOrdersToLoad && !this.pendingOrdersFetching && !this.changingSortType && !this.errorFetchingMoreOrders;
	}

	/**
	 * Return a human-readable relative time string for a given date string.
	 *
	 * @param {string} dateString
	 * @return {string}
	 */
	private getOrderSubmittedDate(dateString: string): string {
		return `${this.$t('orders.dashboard.pending_orders.submitted')} ${getTimeAgo(dateString)}`;
	}

	/**
	 * Determine order type and corresponding icon based on order properties
	 * Handles various order types: catering, delivery, pickup, dine-in, notification, school
	 *
	 * @param {DashboardOrder | undefined} order
	 * @return {ParentOrderType}
	 */
	private getOrderType(order: DashboardOrder | undefined): ParentOrderType {
		if (order) {
			if (order.type) {
				if (order.type === 'catering') {
					return { type: 'catering', icon: 'mdi-stadium-outline' };
				}
				else if (order.type === 'generic-catering') {
					if (order.cateringEventName) {
						return { type: 'catering_event', icon: 'mdi-calendar-month-outline' };
					}
					else if (order.isDelivery) {
						return { type: 'delivery', icon: 'mdi-truck-outline' };
					}
					return { type: 'takeout', icon: 'mdi-food-takeout-box-outline' };
				}
				else if (order.type === 'take-out') {
					if (order.isDelivery) {
						return { type: 'delivery', icon: 'mdi-truck-outline' };
					}
					return { type: 'pickup', icon: 'mdi-food-takeout-box-outline' };
				}
				else if (order.type === 'notification') {
					return { type: 'notification', icon: 'mdi-bell-outline' };
				}
				else if (order.type === 'school') {
					return { type: 'school', icon: 'mdi-school' };
				}
				else {
					return { type: 'dine_in', icon: 'mdi-silverware' };
				}
			}
		}
		return { type: 'dine_in', icon: 'mdi-silverware' };
	}

	/**
	 * Component initialization - set up overflow handling and window resize listener
	 *
	 * @return {void}
	 */
	private mounted(): void {
		this.sortBy = this.pendingOrderSort;
		this.updateListMaxHeight();
		window.addEventListener('resize', this.handleResize);
		this.$refs.pendingOrdersList?.addEventListener('scroll', this.handleListScroll);
	}

	/**
	 * Cleanup resize event listener when component is destroyed
	 *
	 * @return {void}
	 */
	private beforeDestroy(): void {
		window.removeEventListener('resize', this.handleResize);
		this.$refs.pendingOrdersList?.removeEventListener('scroll', this.handleListScroll);
		cancelDebounce(this.handleListScroll);
	}

	private handleResize(): void {
		this.updateListMaxHeight();
	}

	@Debounce(200)
	private handleListScroll(): void {
		this.fetchMoreOrdersOnScroll();
	}

	/**
	 * Update sort order and refresh overflow visibility after DOM update
	 *
	 * @param {'created_at' | 'pickup_time'} sortBy
	 * @return {Promise<void>}
	 */
	private async updateSort(sortBy: 'created_at' | 'pickup_time'): Promise<void> {
		this.changingSortType = true;

		// Prevents a scroll bar from being shown when loading
		this.$refs.pendingOrdersList.style.height = '50px';
		try {
			await this.setPendingOrdersSort(sortBy);
			this.$refs.pendingOrdersList.scrollTo(0, 0);
		}
		catch (errorMessage) {
			this.errorFetchingMoreOrders = true;
			Toast.error(this.$t('orders.dashboard.error_fetching_pending_orders', { errorMessage }));
		}
		finally {
			this.changingSortType = false;
			this.$refs.pendingOrdersList.style.height = 'auto';
		}
	}

	/**
	 * Fetches more pending orders when the user scrolls close to the bottom of the list
	 *
	 * @return {Promise<void>}
	 */
	private async fetchMoreOrdersOnScroll(): Promise<void> {
		const listEnd = this.$refs.pendingOrderLoading;
		if (this.loadMoreOrdersOnScroll && listEnd && listEnd.getBoundingClientRect().top - 250 <= document.body.clientHeight) {
			this.pendingOrdersFetching = true;
			try {
				await this.setPendingOrdersOffset(this.pendingOrders.length);
			}
			catch (errorMessage) {
				this.errorFetchingMoreOrders = true;
				Toast.error(this.$t('orders.dashboard.error_fetching_pending_orders', { errorMessage }));
			}
			finally {
				this.pendingOrdersFetching = false;
			}
		}
	}

	/**
	 * Clear the fetching error and attempt to fetch again
	 * This is for the load more button that is shown at the bottom of the list if there is an error fetching
	 *
	 * @return {void}
	 */
	private clearErrorAndRefetch(): void {
		this.errorFetchingMoreOrders = false;
		this.fetchMoreOrdersOnScroll();
	}

	/**
	 * Set max height of pending order list, above mobile sizes set height to bottom of page
	 *
	 * @return {void}
	 */
	private updateListMaxHeight(): void {
		const list = this.$refs.pendingOrdersList;
		const dashboard = document.getElementById('dashboard');
		const parentContainerWidth = dashboard?.offsetWidth;
		const isInSidebar = !!this.$el.closest('.dashboard-sidebar');

		if (isInSidebar && list) {
			list.style.maxHeight = 'none';
		}
		else if (list && parentContainerWidth && parentContainerWidth >= 1200) {
			const top = list.getBoundingClientRect().top;
			list.style.maxHeight = `calc(100vh - ${top}px - 16px)`;
		}
		else if (list) {
			list.style.maxHeight = '500px';
		}
	}
}
